import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Move, ChevronLeft, ChevronRight } from "lucide-react";
import { showroom } from "../mock";

export default function Showroom360() {
  const navigate = useNavigate();
  const [zoneIdx, setZoneIdx] = useState(0);
  const stripRef = useRef(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);

  const zone = showroom.zones[zoneIdx];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (stripRef.current) stripRef.current.scrollLeft = 0;
  }, [zoneIdx]);

  const onDown = (clientX) => {
    dragging.current = true;
    startX.current = clientX;
    scrollStart.current = stripRef.current?.scrollLeft || 0;
  };
  const onMove = useCallback((clientX) => {
    if (!dragging.current || !stripRef.current) return;
    stripRef.current.scrollLeft = scrollStart.current - (clientX - startX.current);
  }, []);
  const onUp = () => (dragging.current = false);

  useEffect(() => {
    const mm = (e) => onMove(e.clientX);
    const tm = (e) => onMove(e.touches[0].clientX);
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", tm);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", tm);
      window.removeEventListener("touchend", onUp);
    };
  }, [onMove]);

  const scrollBy = (dir) => {
    stripRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0d1640] flex flex-col relative overflow-hidden">
      {/* Immersive blurred backdrop of the current zone wall */}
      <div className="absolute inset-0">
        <img src={zone.wall} alt="" className="w-full h-full object-cover opacity-30 blur-xl scale-110" />
        <div className="absolute inset-0 bg-[#0d1640]/80" />
      </div>

      {/* Top bar */}
      <div className="relative z-20 flex items-center justify-between px-5 sm:px-8 py-5">
        <button onClick={() => navigate("/")} className="kse-btn text-white border border-white/30 px-5 py-2.5 hover:bg-white/10">
          <ArrowLeft size={18} /> Back
        </button>
        <div className="flex items-center gap-2 text-white">
          <span className="font-extrabold text-xl">KSE</span>
          <span className="text-[10px] text-blue-200 tracking-[0.3em] uppercase">Inc.</span>
        </div>
      </div>

      <div className="relative z-20 px-5 sm:px-8 text-center">
        <h1 className="text-white font-extrabold text-3xl sm:text-4xl">{showroom.title}</h1>
        <p className="text-blue-100/70 max-w-2xl mx-auto mt-3 text-sm sm:text-base">{showroom.subtitle}</p>
      </div>

      {/* Zone tabs */}
      <div className="relative z-20 flex flex-wrap justify-center gap-2.5 px-4 mt-7">
        {showroom.zones.map((z, i) => (
          <button
            key={z.id}
            onClick={() => setZoneIdx(i)}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${
              zoneIdx === i
                ? "bg-white text-[#1a2a6b] border-white"
                : "text-white border-white/30 hover:bg-white/10"
            }`}
          >
            {z.label}
          </button>
        ))}
      </div>

      {/* Draggable garment gallery (walk-past showroom) */}
      <div className="relative z-20 flex-1 flex items-center mt-8 mb-6">
        <button onClick={() => scrollBy(-1)} className="hidden sm:flex absolute left-4 z-30 w-12 h-12 rounded-full bg-white/90 text-[#1a2a6b] items-center justify-center shadow-lg hover:bg-white">
          <ChevronLeft size={24} />
        </button>

        <div
          ref={stripRef}
          onMouseDown={(e) => onDown(e.clientX)}
          onTouchStart={(e) => onDown(e.touches[0].clientX)}
          className="flex gap-6 overflow-x-auto px-6 sm:px-20 pb-10 w-full cursor-grab active:cursor-grabbing select-none scrollbar-hide"
          style={{ scrollbarWidth: "none" }}
        >
          {zone.garments.map((g, i) => (
            <div key={g.name + i} className="shrink-0 w-[240px] sm:w-[300px]">
              <div className="group relative rounded-3xl overflow-hidden h-[340px] sm:h-[420px] shadow-2xl bg-white">
                <img src={g.image} alt={g.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" draggable={false} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1640]/85 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-5">
                  <h3 className="text-white font-bold text-lg leading-tight">{g.name}</h3>
                  <p className="text-blue-200 text-sm mt-1">{g.fabric}</p>
                </div>
              </div>
              {/* reflection for 3D feel */}
              <div className="h-16 mx-4 rounded-b-3xl bg-gradient-to-b from-white/15 to-transparent blur-[2px] opacity-40"
                   style={{ transform: "scaleY(-1)" }} />
            </div>
          ))}
        </div>

        <button onClick={() => scrollBy(1)} className="hidden sm:flex absolute right-4 z-30 w-12 h-12 rounded-full bg-white/90 text-[#1a2a6b] items-center justify-center shadow-lg hover:bg-white">
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Drag hint */}
      <div className="relative z-20 flex items-center justify-center gap-2 text-white/70 text-sm pb-8">
        <Move size={16} /> Drag or swipe to explore the {zone.label} display
      </div>
    </div>
  );
}
